import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { KeyRound, MailCheck } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import type * as S from "@releo/shared";
import { LanguageMenu } from "@/components/language-menu";
import { Logo } from "@/components/layout";
import { Button, Card, ErrorBox, Field, Input, SuccessBox } from "@/components/ui";
import { ApiError, authPost, http } from "@/lib/api";
import { keys, useAuthStatus } from "@/lib/queries";

export function AuthCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-full items-center justify-center px-4 py-12">
      <div className="absolute right-4 top-4">
        <LanguageMenu />
      </div>
      <div className="w-full max-w-sm space-y-6">
        <div className="flex justify-center">
          <Logo />
        </div>
        <Card className="p-6">
          <h1 className="text-lg font-semibold">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-fg-muted">{subtitle}</p>}
          <div className="mt-5">{children}</div>
        </Card>
      </div>
    </div>
  );
}

export function safeRedirect(value: unknown): string | null {
  return typeof value === "string" && value.startsWith("/") && !value.startsWith("//") ? value : null;
}

export function useAuthForm(path: string, onPending?: (pending: S.VerificationPending) => void) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    setBusy(true);
    setError(null);
    try {
      const result = await http.post<S.Me | S.VerificationPending>(path, data);
      if ("verificationRequired" in result) {
        onPending?.(result);
        return;
      }
      qc.setQueryData(keys.me, result);
      await qc.invalidateQueries({ queryKey: keys.status });
      await navigate({ to: safeRedirect(search.redirect) ?? "/apps" });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return { submit, error, busy };
}

function CheckInbox({ email, onBack }: { email: string; onBack: () => void }) {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const resend = async () => {
    setBusy(true);
    setError(null);
    try {
      await authPost("/send-verification-email", { email, callbackURL: "/login?verified=1" });
      setSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthCard title={t("auth.checkInboxTitle")}>
      <div className="space-y-4">
        <div className="flex justify-center text-brand-600">
          <MailCheck className="h-10 w-10" />
        </div>
        <p className="text-sm text-fg-muted">{t("auth.checkInboxHelp", { email })}</p>
        {sent && <SuccessBox>{t("auth.verificationResent")}</SuccessBox>}
        <ErrorBox error={error} />
        <Button type="button" variant="outline" className="w-full justify-center" loading={busy} onClick={() => void resend()}>
          {t("auth.resendVerification")}
        </Button>
        <button type="button" className="w-full text-center text-sm text-fg-muted hover:underline" onClick={onBack}>
          {t("auth.backToLogin")}
        </button>
      </div>
    </AuthCard>
  );
}

export function SetupPage() {
  const { t } = useTranslation();
  const { submit, error, busy } = useAuthForm("/auth/setup");
  return (
    <AuthCard title={t("auth.setupTitle")} subtitle={t("auth.setupSubtitle")}>
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("common.name")} htmlFor="name">
          <Input id="name" name="name" required autoComplete="name" />
        </Field>
        <Field label={t("common.email")} htmlFor="email">
          <Input id="email" name="email" type="email" required autoComplete="email" />
        </Field>
        <Field label={t("common.password")} help={t("auth.passwordHint")} htmlFor="password">
          <Input id="password" name="password" type="password" minLength={10} required autoComplete="new-password" />
        </Field>
        <ErrorBox error={error} />
        <Button type="submit" className="w-full justify-center" loading={busy}>
          {t("auth.setupSubmit")}
        </Button>
      </form>
    </AuthCard>
  );
}

/** Starts single sign-on: better-auth returns the identity provider URL for the email domain. */
export async function startSso(email: string, callbackURL = "/apps"): Promise<string> {
  const res = await fetch("/api/auth/sign-in/sso", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, callbackURL, errorCallbackURL: "/login" }),
  });
  const data = (await res.json().catch(() => null)) as { url?: string; message?: string; code?: string } | null;
  if (!res.ok || !data?.url) throw new ApiError(res.status, data?.code ?? "sso_error", data?.message ?? res.statusText);
  return data.url;
}

export function LoginPage() {
  const { t } = useTranslation();
  const status = useAuthStatus();
  const search = useSearch({ strict: false }) as { error?: string; verified?: unknown; reset?: unknown; redirect?: string };
  const [email, setEmail] = useState("");
  const [unverified, setUnverified] = useState<string | null>(null);
  const [sso, setSso] = useState(false);
  const [ssoError, setSsoError] = useState<unknown>(null);
  const [ssoBusy, setSsoBusy] = useState(false);
  const { submit, error, busy } = useAuthForm("/auth/login");
  // The organization requires SSO for this email: switch to the SSO form.
  useEffect(() => {
    if (error instanceof ApiError && error.code === "sso_required") setSso(true);
    if (error instanceof ApiError && error.code === "email_not_verified") setUnverified(email);
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

  const callbackError = search.error
    ? search.error === "account not linked"
      ? t("auth.ssoNotLinked")
      : /token/i.test(search.error)
        ? t("auth.linkInvalid")
        : t("auth.ssoFailed")
    : null;

  if (unverified) return <CheckInbox email={unverified} onBack={() => setUnverified(null)} />;

  if (sso) {
    return (
      <AuthCard title={t("auth.ssoTitle")} subtitle={t("auth.ssoSubtitle")}>
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setSsoBusy(true);
            setSsoError(null);
            try {
              window.location.assign(await startSso(email, safeRedirect(search.redirect) ?? "/apps"));
            } catch (err) {
              setSsoError(err instanceof ApiError && err.status === 404 ? new Error(t("auth.ssoNoProvider")) : err);
              setSsoBusy(false);
            }
          }}
        >
          <Field label={t("common.email")} htmlFor="sso-email">
            <Input id="sso-email" type="email" required autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <ErrorBox error={ssoError} />
          <Button type="submit" className="w-full justify-center" loading={ssoBusy}>
            {t("auth.ssoSubmit")}
          </Button>
          <button type="button" className="w-full text-center text-sm text-fg-muted hover:underline" onClick={() => setSso(false)}>
            {t("auth.ssoBack")}
          </button>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t("auth.loginTitle")}>
      <form onSubmit={submit} className="space-y-4">
        {callbackError && <ErrorBox error={new Error(callbackError)} />}
        {search.verified !== undefined && !callbackError && <SuccessBox>{t("auth.emailVerified")}</SuccessBox>}
        {search.reset !== undefined && <SuccessBox>{t("auth.passwordResetDone")}</SuccessBox>}
        <Field label={t("common.email")} htmlFor="email">
          <Input id="email" name="email" type="email" required autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label={t("common.password")} htmlFor="password">
          <Input id="password" name="password" type="password" required autoComplete="current-password" />
        </Field>
        {status.data?.emailEnabled && (
          <div className="-mt-2 text-right text-sm">
            <Link to="/forgot-password" className="text-brand-600 hover:underline">
              {t("auth.forgotPassword")}
            </Link>
          </div>
        )}
        <ErrorBox error={error} />
        <Button type="submit" className="w-full justify-center" loading={busy}>
          {t("auth.loginSubmit")}
        </Button>
        {status.data?.ssoEnabled && (
          <Button type="button" variant="outline" className="w-full justify-center" onClick={() => setSso(true)}>
            <KeyRound className="h-4 w-4" />
            {t("auth.ssoContinue")}
          </Button>
        )}
        {status.data?.signupEnabled && (
          <p className="text-center text-sm text-fg-muted">
            {t("auth.noAccount")}{" "}
            <Link to="/signup" className="font-medium text-brand-600 hover:underline">
              {t("auth.signup")}
            </Link>
          </p>
        )}
      </form>
    </AuthCard>
  );
}

export function SignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pending, setPending] = useState<string | null>(null);
  const { submit, error, busy } = useAuthForm("/auth/signup", (p) => setPending(p.email));
  if (pending) return <CheckInbox email={pending} onBack={() => void navigate({ to: "/login" })} />;
  return (
    <AuthCard title={t("auth.signupTitle")}>
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("common.name")} htmlFor="name">
          <Input id="name" name="name" required autoComplete="name" />
        </Field>
        <Field label={t("common.email")} htmlFor="email">
          <Input id="email" name="email" type="email" required autoComplete="email" />
        </Field>
        <Field label={t("common.password")} help={t("auth.passwordHint")} htmlFor="password">
          <Input id="password" name="password" type="password" minLength={10} required autoComplete="new-password" />
        </Field>
        <ErrorBox error={error} />
        <Button type="submit" className="w-full justify-center" loading={busy}>
          {t("auth.signupSubmit")}
        </Button>
        <AcceptTerms />
        <p className="text-center text-sm text-fg-muted">
          {t("auth.haveAccount")}{" "}
          <Link to="/login" className="font-medium text-brand-600 hover:underline">
            {t("auth.login")}
          </Link>
        </p>
      </form>
    </AuthCard>
  );
}

function AcceptTerms() {
  const { t } = useTranslation();
  const { termsUrl, privacyUrl } = useAuthStatus().data ?? {};
  if (!termsUrl && !privacyUrl) return null;
  const link = (href: string, label: string) => (
    <a href={href} target="_blank" rel="noreferrer" className="font-medium text-brand-600 hover:underline">
      {label}
    </a>
  );
  return (
    <p className="text-center text-xs text-fg-muted">
      {t("auth.acceptPrefix")} {termsUrl && link(termsUrl, t("auth.acceptTerms"))}
      {termsUrl && privacyUrl && ` ${t("auth.acceptAnd")} `}
      {privacyUrl && link(privacyUrl, t("auth.acceptPrivacy"))}.
    </p>
  );
}
